import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import { ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { groundClassifyTrackingAtom } from '@/atoms/groundClassify-progress.atom';
import { layersAtom } from '@/atoms/layers.atom';
import { execute } from '@/graphql/execute';
import type { LayersQuery } from '@/graphql/graphql';
import { useDialog } from '@/providers/DialogProvider';
import { startGroundClassification } from '@/queries/groundClassify.query';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

type LayerData = LayersQuery['layers'][number];

/**
 * Numeric input backed by a local string so the field can hold
 * intermediate states like "0.", "0", or "" while the user is
 * typing. A controlled `type=number` input snaps the displayed
 * value back from the parsed float on every keystroke, which
 * drops trailing dots/zeros and makes typing "0", ".", "5"
 * left-to-right unable to ever reach 0.5. We use type=text +
 * inputMode=decimal so mobile keyboards still get the numeric
 * pad without the browser eating partial inputs.
 */
function NumberField({
  value,
  onChange,
  integer = false,
  ...props
}: {
  value: number;
  onChange: (v: number) => void;
  integer?: boolean;
} & Omit<React.ComponentProps<typeof Input>, 'value' | 'onChange' | 'type'>) {
  const parse = integer ? (s: string) => parseInt(s, 10) : parseFloat;
  const [text, setText] = useState(() => String(value));
  // Sync from external value (form reset, programmatic update) only
  // when the displayed text doesn't already represent the same
  // number — otherwise we'd clobber "0." while the user is typing
  // because parseFloat("0.") === 0 === current form value.
  useEffect(() => {
    const parsed = parse(text);
    if (!Number.isFinite(parsed) || parsed !== value) {
      setText(String(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <Input
      type='text'
      inputMode='decimal'
      value={text}
      onChange={(e) => {
        const v = e.target.value;
        setText(v);
        const parsed = parse(v);
        if (Number.isFinite(parsed)) onChange(parsed);
      }}
      {...props}
    />
  );
}

interface Props {
  sourceLayer: LayerData;
  projectId: string;
}

// Colour ramps mirrored from scripts/las2ground.py's _RAMPS dict. Keep this
// list in sync — the worker tolerates unknown names (falls back to viridis)
// but the dropdown should only offer values that actually render correctly.
const COLORMAPS = ['viridis', 'cividis', 'magma', 'terrain', 'greyscale'] as const;
type Colormap = (typeof COLORMAPS)[number];

interface FormData {
  name: string;
  boundaryLayerId: string;
  algorithm: 'smrf' | 'csf';
  resolution: number;
  rasterOutputType: 'idw' | 'mean' | 'min' | 'max';
  // Pre-processing toggles
  runElm: boolean;
  runOutlier: boolean;
  resetClassification: boolean;
  classifyAllReturns: boolean;
  // Optional outputs
  writeCount: boolean;
  writeRgbDtm: boolean;
  colormap: Colormap;
  // SMRF
  smrfCell: number;
  smrfScalar: number;
  smrfSlope: number;
  smrfThreshold: number;
  smrfWindow: number;
  smrfCut: number;
  // CSF
  csfResolution: number;
  csfThreshold: number;
  csfHdiff: number;
  csfSmooth: boolean;
  csfStep: number;
  csfRigidness: number;
  csfIterations: number;
}

/**
 * Dialog for kicking off a ground-classification job from an existing
 * POINTCLOUD layer's context menu. Mirrors RunDeviationAnalysisDialog: form
 * → typed mutation → synthetic batch → preprocess → ECS worker.
 *
 * The full SMRF/CSF advanced parameter surface is exposed under a
 * Collapsible (closed by default) so first-time users see only the two
 * essentials (algorithm + resolution) and power users can drill into the
 * tuning knobs without leaving the dialog.
 */
export function RunGroundClassificationDialog({ sourceLayer, projectId }: Props) {
  const { t } = useTranslation('project');
  const { dismissActiveDialog } = useDialog();
  const queryClient = useQueryClient();
  const [, setTrackers] = useAtom(groundClassifyTrackingAtom);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const allLayers = useAtomValue(layersAtom);
  const boundaryCandidates = useMemo(
    () => allLayers.filter((l) => l.type === 'VECTOR' && l.id !== sourceLayer.id),
    [allLayers, sourceLayer.id],
  );

  const formSchema = z.object({
    name: z.string().min(1),
    boundaryLayerId: z.string().default(''),
    algorithm: z.enum(['smrf', 'csf']),
    resolution: z.number().positive(),
    rasterOutputType: z.enum(['idw', 'mean', 'min', 'max']),
    runElm: z.boolean(),
    runOutlier: z.boolean(),
    resetClassification: z.boolean(),
    classifyAllReturns: z.boolean(),
    writeCount: z.boolean(),
    writeRgbDtm: z.boolean(),
    colormap: z.enum(COLORMAPS),
    smrfCell: z.number().positive(),
    smrfScalar: z.number().positive(),
    smrfSlope: z.number().min(0),
    smrfThreshold: z.number().positive(),
    smrfWindow: z.number().positive(),
    smrfCut: z.number().min(0),
    csfResolution: z.number().positive(),
    csfThreshold: z.number().positive(),
    csfHdiff: z.number().min(0),
    csfSmooth: z.boolean(),
    csfStep: z.number().positive(),
    csfRigidness: z.number().int().min(1).max(3),
    csfIterations: z.number().int().min(50),
  });

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    // Defaults match scripts/las2ground.py CLI defaults so a power user's
    // CLI first-pass values match the dialog's first-pass values.
    defaultValues: {
      name: `${sourceLayer.name} DTM`,
      boundaryLayerId: '',
      algorithm: 'smrf',
      resolution: 1.0,
      // Default `mean` is the fast path: numpy 2-D bincount instead of
      // PDAL's single-threaded IDW writer. Equivalent quality on dense
      // ground clouds (which is the typical input here); users who
      // need real interpolation for sparse / gappy data switch to IDW.
      rasterOutputType: 'mean',
      runElm: false,
      runOutlier: false,
      resetClassification: false,
      classifyAllReturns: false,
      writeCount: false,
      // RGB DTM companion is on by default — without it the DTM is
      // download-only because gdal2tiles refuses Float32 input.
      writeRgbDtm: true,
      // Default colormap. viridis is perceptually uniform and
      // colourblind-safe, which matters for the "tile shown on a 2D
      // map" use-case. Hypsometric `terrain` is available for users
      // who specifically want the topographic blue→white look.
      colormap: 'viridis',
      smrfCell: 1.0,
      smrfScalar: 1.2,
      smrfSlope: 0.2,
      smrfThreshold: 0.45,
      smrfWindow: 16.0,
      smrfCut: 0.0,
      csfResolution: 1.0,
      csfThreshold: 0.5,
      csfHdiff: 0.3,
      csfSmooth: true,
      csfStep: 0.65,
      csfRigidness: 3,
      csfIterations: 500,
    },
  });

  const watchedAlgorithm = form.watch('algorithm');

  const mutation = useMutation({
    mutationFn: async (vars: FormData) => {
      return execute(startGroundClassification, {
        input: {
          sourceLayerId: sourceLayer.id,
          name: vars.name || undefined,
          boundaryLayerId: vars.boundaryLayerId || undefined,
          params: {
            algorithm: vars.algorithm,
            resolution: vars.resolution,
            rasterOutputType: vars.rasterOutputType,
            runElm: vars.runElm,
            runOutlier: vars.runOutlier,
            resetClassification: vars.resetClassification,
            classifyAllReturns: vars.classifyAllReturns,
            writeCount: vars.writeCount,
            writeRgbDtm: vars.writeRgbDtm,
            colormap: vars.colormap,
            smrfCell: vars.smrfCell,
            smrfScalar: vars.smrfScalar,
            smrfSlope: vars.smrfSlope,
            smrfThreshold: vars.smrfThreshold,
            smrfWindow: vars.smrfWindow,
            smrfCut: vars.smrfCut,
            csfResolution: vars.csfResolution,
            csfThreshold: vars.csfThreshold,
            csfHdiff: vars.csfHdiff,
            csfSmooth: vars.csfSmooth,
            csfStep: vars.csfStep,
            csfRigidness: vars.csfRigidness,
            csfIterations: vars.csfIterations,
          },
        },
      });
    },
    onSuccess: (data) => {
      const batchId = data.startGroundClassification;
      // Open a long-lived loading toast (`duration: Infinity` — won't
      // auto-dismiss) and hand it to the project-shell tracker hook.
      // The hook resolves it into either a success or a failure toast
      // when the run actually finishes server-side, so the user's
      // visible state matches reality (mirrors `useDeviationProgress`).
      const toastId = toast.loading(t('groundClassification.runningToast', { name: sourceLayer.name }), {
        duration: Infinity,
      });
      // Snapshot existing layers derived from this source so the hook
      // can detect "the new one this run produces" by exclusion. Same
      // disambiguation strategy deviation uses; survives client/server
      // clock skew that breaks naive timestamp comparisons.
      const cachedLayers = queryClient.getQueryData<{ layers: LayerData[] }>(['layers', projectId]);
      const existingDerivedLayerIds = (cachedLayers?.layers ?? [])
        .filter((l) => {
          const meta = (l.metadata ?? {}) as { derivedFrom?: string };
          return meta.derivedFrom === sourceLayer.id;
        })
        .map((l) => l.id);
      setTrackers((prev) => [
        ...prev,
        {
          toastId,
          batchId,
          projectId,
          sourceLayerId: sourceLayer.id,
          sourceLayerName: sourceLayer.name,
          existingDerivedLayerIds,
        },
      ]);
      dismissActiveDialog();
    },
    onError: (err: Error) => {
      toast.error(err.message || t('groundClassification.failedToStart'));
    },
  });

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('groundClassification.title')}</DialogTitle>
        <DialogDescription>
          {t('groundClassification.sourceLabel')}: <strong>{sourceLayer.name}</strong>
        </DialogDescription>
      </DialogHeader>

      <Form {...form}>
        <form onSubmit={form.handleSubmit((vars) => mutation.mutate(vars))} className='flex max-h-[80vh] flex-col'>
          <div className='flex-1 space-y-4 overflow-y-auto px-1 py-1'>
            <FormField
              control={form.control}
              name='name'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('groundClassification.outputName')}</FormLabel>
                  <FormControl>
                    <Input type='text' {...field} />
                  </FormControl>
                  <FormDescription>{t('groundClassification.outputNameHelp')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='boundaryLayerId'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('boundary.label')}</FormLabel>
                  <Select
                    onValueChange={(v) => field.onChange(v === '__none__' ? '' : v)}
                    value={field.value || '__none__'}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value='__none__'>{t('boundary.none')}</SelectItem>
                      {boundaryCandidates.map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>{t('boundary.help')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Algorithm — radio (not select) because there are only two
                options and they have meaningfully different semantics, so
                a radio group with descriptions reads better. */}
            <FormField
              control={form.control}
              name='algorithm'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('groundClassification.algorithm')}</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className='flex flex-col gap-2'>
                      {(['smrf', 'csf'] as const).map((algo) => (
                        <label key={algo} className='flex cursor-pointer items-start gap-2'>
                          <RadioGroupItem value={algo} id={`algo-${algo}`} />
                          <div>
                            <div className='text-sm font-medium'>
                              {t(`groundClassification.algorithms.${algo}.label`)}
                            </div>
                            <div className='text-xs text-slate-600 dark:text-stone-400'>
                              {t(`groundClassification.algorithms.${algo}.description`)}
                            </div>
                          </div>
                        </label>
                      ))}
                    </RadioGroup>
                  </FormControl>
                </FormItem>
              )}
            />

            <div className='grid grid-cols-2 gap-3'>
              <FormField
                control={form.control}
                name='resolution'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('groundClassification.resolution')}</FormLabel>
                    <FormControl>
                      <NumberField value={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormDescription>{t('groundClassification.resolutionHelp')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='rasterOutputType'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('groundClassification.rasterOutputType')}</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value='idw'>IDW</SelectItem>
                        <SelectItem value='mean'>Mean</SelectItem>
                        <SelectItem value='min'>Min</SelectItem>
                        <SelectItem value='max'>Max</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>{t('groundClassification.rasterOutputTypeHelp')}</FormDescription>
                  </FormItem>
                )}
              />
            </div>

            {/* RGB DTM toggle is exposed at top-level (not inside Advanced)
                because turning it off has user-visible consequences (no
                terrain layer in the 3D viewer). */}
            <FormField
              control={form.control}
              name='writeRgbDtm'
              render={({ field }) => (
                <FormItem className='flex items-center gap-2'>
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div>
                    <FormLabel className='!mt-0'>{t('groundClassification.writeRgbDtm')}</FormLabel>
                    <FormDescription>{t('groundClassification.writeRgbDtmHelp')}</FormDescription>
                  </div>
                </FormItem>
              )}
            />

            {/* Colormap dropdown is paired with the RGB DTM toggle —
                picking a ramp is meaningless when the RGB output is
                disabled, so we hide the dropdown in that case rather
                than disabling it (less visual clutter). */}
            {form.watch('writeRgbDtm') && (
              <FormField
                control={form.control}
                name='colormap'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('groundClassification.colormap')}</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {COLORMAPS.map((cm) => (
                          <SelectItem key={cm} value={cm}>
                            {t(`groundClassification.colormaps.${cm}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>{t('groundClassification.colormapHelp')}</FormDescription>
                  </FormItem>
                )}
              />
            )}

            <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
              <CollapsibleTrigger asChild>
                <Button variant='ghost' type='button' className='w-full justify-between'>
                  <span>{t('groundClassification.advanced')}</span>
                  <ChevronDown className={`size-4 transition-transform ${advancedOpen ? 'rotate-180' : ''}`} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className='space-y-3 pt-2'>
                {/* Pre-processing block. Each toggle gets a one-line
                    FormDescription so users tuning a noisy scan can read
                    why each pre-filter exists without leaving the dialog.
                    Block-level intro at the top frames the category. */}
                <div className='space-y-3 rounded-md border border-slate-200/70 p-3 dark:border-stone-700/40'>
                  <div>
                    <FormLabel className='text-sm font-medium'>{t('groundClassification.preprocessing')}</FormLabel>
                    <p className='mt-1 text-xs text-slate-600 dark:text-stone-400'>
                      {t('groundClassification.preprocessingHelp')}
                    </p>
                  </div>
                  <FormField
                    control={form.control}
                    name='runElm'
                    render={({ field }) => (
                      <FormItem>
                        <div className='flex items-center gap-2'>
                          <FormControl>
                            <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                          <FormLabel className='!mt-0'>{t('groundClassification.runElm')}</FormLabel>
                        </div>
                        <FormDescription>{t('groundClassification.runElmHelp')}</FormDescription>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name='runOutlier'
                    render={({ field }) => (
                      <FormItem>
                        <div className='flex items-center gap-2'>
                          <FormControl>
                            <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                          <FormLabel className='!mt-0'>{t('groundClassification.runOutlier')}</FormLabel>
                        </div>
                        <FormDescription>{t('groundClassification.runOutlierHelp')}</FormDescription>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name='resetClassification'
                    render={({ field }) => (
                      <FormItem>
                        <div className='flex items-center gap-2'>
                          <FormControl>
                            <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                          <FormLabel className='!mt-0'>{t('groundClassification.resetClassification')}</FormLabel>
                        </div>
                        <FormDescription>{t('groundClassification.resetClassificationHelp')}</FormDescription>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name='classifyAllReturns'
                    render={({ field }) => (
                      <FormItem>
                        <div className='flex items-center gap-2'>
                          <FormControl>
                            <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                          <FormLabel className='!mt-0'>{t('groundClassification.classifyAllReturns')}</FormLabel>
                        </div>
                        <FormDescription>{t('groundClassification.classifyAllReturnsHelp')}</FormDescription>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name='writeCount'
                    render={({ field }) => (
                      <FormItem>
                        <div className='flex items-center gap-2'>
                          <FormControl>
                            <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                          <FormLabel className='!mt-0'>{t('groundClassification.writeCount')}</FormLabel>
                        </div>
                      </FormItem>
                    )}
                  />
                </div>

                {/* SMRF block. Active only when algorithm=smrf. The
                    intro paragraph at the top describes what SMRF is
                    doing internally so users tuning a knob have the
                    physical intuition; per-field FormDescription gives
                    the one-line "what does this knob do" reminder. */}
                {watchedAlgorithm === 'smrf' && (
                  <div className='space-y-3 rounded-md border border-slate-200/70 p-3 dark:border-stone-700/40'>
                    <div>
                      <FormLabel className='text-sm font-medium'>SMRF</FormLabel>
                      <p className='mt-1 text-xs text-slate-600 dark:text-stone-400'>
                        {t('groundClassification.smrfHelp')}
                      </p>
                    </div>
                    <div className='grid grid-cols-2 gap-3'>
                      {(['smrfCell', 'smrfScalar', 'smrfSlope', 'smrfThreshold', 'smrfWindow', 'smrfCut'] as const).map(
                        (name) => (
                          <FormField
                            key={name}
                            control={form.control}
                            name={name}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t(`groundClassification.${name}`)}</FormLabel>
                                <FormControl>
                                  <NumberField value={field.value} onChange={field.onChange} />
                                </FormControl>
                                <FormDescription>{t(`groundClassification.${name}Help`)}</FormDescription>
                              </FormItem>
                            )}
                          />
                        ),
                      )}
                    </div>
                  </div>
                )}

                {/* CSF block. Active only when algorithm=csf. Same shape
                    as the SMRF block above — intro paragraph framing the
                    cloth-simulation mental model, per-field
                    FormDescription on each knob. */}
                {watchedAlgorithm === 'csf' && (
                  <div className='space-y-3 rounded-md border border-slate-200/70 p-3 dark:border-stone-700/40'>
                    <div>
                      <FormLabel className='text-sm font-medium'>CSF</FormLabel>
                      <p className='mt-1 text-xs text-slate-600 dark:text-stone-400'>
                        {t('groundClassification.csfHelp')}
                      </p>
                    </div>
                    <div className='grid grid-cols-2 gap-3'>
                      {(['csfResolution', 'csfThreshold', 'csfHdiff', 'csfStep'] as const).map((name) => (
                        <FormField
                          key={name}
                          control={form.control}
                          name={name}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t(`groundClassification.${name}`)}</FormLabel>
                              <FormControl>
                                <NumberField value={field.value} onChange={field.onChange} />
                              </FormControl>
                              <FormDescription>{t(`groundClassification.${name}Help`)}</FormDescription>
                            </FormItem>
                          )}
                        />
                      ))}
                      <FormField
                        control={form.control}
                        name='csfRigidness'
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('groundClassification.csfRigidness')}</FormLabel>
                            <FormControl>
                              <NumberField value={field.value} onChange={field.onChange} integer />
                            </FormControl>
                            <FormDescription>{t('groundClassification.csfRigidnessHelp')}</FormDescription>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name='csfIterations'
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('groundClassification.csfIterations')}</FormLabel>
                            <FormControl>
                              <NumberField value={field.value} onChange={field.onChange} integer />
                            </FormControl>
                            <FormDescription>{t('groundClassification.csfIterationsHelp')}</FormDescription>
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name='csfSmooth'
                      render={({ field }) => (
                        <FormItem>
                          <div className='flex items-center gap-2'>
                            <FormControl>
                              <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                            <FormLabel className='!mt-0'>{t('groundClassification.csfSmooth')}</FormLabel>
                          </div>
                          <FormDescription>{t('groundClassification.csfSmoothHelp')}</FormDescription>
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          </div>

          <DialogFooter className='mt-4 shrink-0 border-t border-slate-200/70 pt-4 dark:border-stone-700/40'>
            <Button variant='outline' type='button' onClick={() => dismissActiveDialog()} disabled={mutation.isPending}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type='submit' disabled={mutation.isPending}>
              {mutation.isPending ? t('groundClassification.starting') : t('groundClassification.run')}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
}
